import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  AppSvgIconComponent,
  BaseOverlayComponent,
  OverlayService,
  SignalButton,
  SignalDropdown,
  SignalRadio,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryCollectionFieldResponse } from '@core/api/workspace/models/dataLibraryCollectionFieldResponse';
import type { GetDataLibraryCollectionApiResponse } from '@core/api/workspace/models/getDataLibraryCollectionApiResponse';
import {
  resolveCollectionDropdownItems,
  type CollectionSelectOption,
} from '../../utils/resolve-collection-dropdown-items';
import { CollectionDataPreviewSheetComponent } from '../collection-data-preview-sheet/collection-data-preview-sheet';

export type CollectionColumnSelectionMode = 'single' | 'primarySecondary';

export interface CollectionColumnPickerOverlayData {
  collectionId: string;
  collectionName?: string;
  selectionMode?: CollectionColumnSelectionMode;
  selectedDisplayFieldKey?: string;
  selectedSecondaryDisplayFieldKey?: string;
  selectedDefaultItemId?: string;
}

export interface CollectionColumnPickerResult {
  displayFieldKey: string;
  displayFieldLabel: string;
  secondaryDisplayFieldKey?: string;
  secondaryDisplayFieldLabel?: string;
  /** Collection item id used as the field default, if any. */
  defaultItemId?: string;
}

interface ColumnOption {
  key: string;
  label: string;
}

@Component({
  selector: 'app-collection-column-picker-overlay',
  standalone: true,
  imports: [
    AppSvgIconComponent,
    BaseOverlayComponent,
    SignalButton,
    SignalDropdown,
    SignalRadio,
  ],
  templateUrl: './collection-column-picker-overlay.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionColumnPickerOverlayComponent
  extends BaseComponent
  implements OnInit
{
  private readonly dialogRef = inject(
    DialogRef<CollectionColumnPickerResult | null>,
  );
  private readonly dialogData = inject<CollectionColumnPickerOverlayData>(
    DIALOG_DATA,
  );
  private readonly workspaceApi = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);

  readonly loadState = new State<GetDataLibraryCollectionApiResponse>();
  readonly columns = signal<ColumnOption[]>([]);
  readonly selectedKey = signal('');
  readonly selectedSecondaryKey = signal('');
  readonly defaultItems = signal<CollectionSelectOption[]>([]);
  readonly selectedDefaultItemId = signal<string | null>(
    this.dialogData.selectedDefaultItemId?.trim() || null,
  );
  readonly defaultsLoading = signal(false);

  readonly selectionMode = computed(
    (): CollectionColumnSelectionMode =>
      this.dialogData.selectionMode === 'primarySecondary'
        ? 'primarySecondary'
        : 'single',
  );

  readonly isPrimarySecondary = computed(
    () => this.selectionMode() === 'primarySecondary',
  );

  readonly title = computed(() =>
    this.isPrimarySecondary()
      ? 'Choose display fields'
      : 'Choose display field',
  );

  readonly description = computed(() =>
    this.isPrimarySecondary()
      ? 'Pick primary and secondary fields shown in the searchable picker.'
      : 'Pick which collection field appears as the option label.',
  );

  readonly isLoading = computed(() => this.loadState.loading());

  readonly isEmpty = computed(
    () => !this.isLoading() && this.columns().length === 0,
  );

  readonly canConfirm = computed(() => {
    const primary = this.selectedKey().trim();
    if (!primary) {
      return false;
    }
    if (!this.isPrimarySecondary()) {
      return true;
    }
    const secondary = this.selectedSecondaryKey().trim();
    return Boolean(secondary) && secondary !== primary;
  });

  constructor() {
    super();
    effect((onCleanup) => {
      const collectionId = this.dialogData.collectionId?.trim();
      const displayFieldKey = this.selectedKey().trim();
      if (!collectionId || !displayFieldKey) {
        this.defaultItems.set([]);
        return;
      }

      this.defaultsLoading.set(true);
      const sub = resolveCollectionDropdownItems(
        this.workspaceApi,
        collectionId,
        displayFieldKey,
      ).subscribe({
        next: (items) => {
          this.defaultItems.set(items);
          this.defaultsLoading.set(false);
          const current = this.selectedDefaultItemId();
          if (current && !items.some((item) => item.id === current)) {
            this.selectedDefaultItemId.set(null);
          }
        },
        error: () => {
          this.defaultItems.set([]);
          this.defaultsLoading.set(false);
        },
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  ngOnInit(): void {
    this.loadColumns();
  }

  onColumnChange(value: unknown): void {
    if (typeof value === 'string' && value.trim()) {
      this.selectedKey.set(value.trim());
    }
  }

  onSecondaryColumnChange(value: unknown): void {
    if (typeof value === 'string' && value.trim()) {
      this.selectedSecondaryKey.set(value.trim());
    }
  }

  onDefaultChange(value: unknown): void {
    if (value == null || value === '') {
      this.selectedDefaultItemId.set(null);
      return;
    }
    if (typeof value === 'string') {
      this.selectedDefaultItemId.set(value.trim() || null);
    }
  }

  openDataPreview(): void {
    const collectionId = this.dialogData.collectionId?.trim();
    if (!collectionId) {
      return;
    }
    this.overlayService.openBottomSheet(CollectionDataPreviewSheetComponent, {
      data: {
        collectionId,
        collectionName: this.dialogData.collectionName,
      },
      panelClass: [
        'bg-white',
        'shadow-2',
        'w-full',
        'h-[90%]',
        'max-h-[90dvh]',
        'rounded-t-3xl',
        'overflow-hidden',
      ],
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    const key = this.selectedKey().trim();
    if (!key || !this.canConfirm()) {
      return;
    }
    const label =
      this.columns().find((column) => column.key === key)?.label || key;
    const defaultItemId = this.selectedDefaultItemId()?.trim() || undefined;

    if (!this.isPrimarySecondary()) {
      this.dialogRef.close({
        displayFieldKey: key,
        displayFieldLabel: label,
        ...(defaultItemId ? { defaultItemId } : {}),
      });
      return;
    }

    const secondaryKey = this.selectedSecondaryKey().trim();
    const secondaryLabel =
      this.columns().find((column) => column.key === secondaryKey)?.label ||
      secondaryKey;
    this.dialogRef.close({
      displayFieldKey: key,
      displayFieldLabel: label,
      secondaryDisplayFieldKey: secondaryKey,
      secondaryDisplayFieldLabel: secondaryLabel,
      ...(defaultItemId ? { defaultItemId } : {}),
    });
  }

  private loadColumns(): void {
    const collectionId = this.dialogData.collectionId?.trim();
    if (!collectionId) {
      return;
    }

    this.executeRequest({
      state: this.loadState,
      request: this.workspaceApi.getDataLibraryCollection(collectionId),
      handleError: true,
      onSuccess: (response: GetDataLibraryCollectionApiResponse) => {
        const data = response.data;
        const columns = (data?.fields ?? []).map(
          (field: DataLibraryCollectionFieldResponse): ColumnOption => ({
            key: field.key,
            label: field.name || field.key,
          }),
        );
        this.columns.set(columns);

        const preferred = this.dialogData.selectedDisplayFieldKey?.trim();
        const nextKey =
          (preferred && columns.some((column) => column.key === preferred)
            ? preferred
            : undefined) ||
          (data?.displayFieldKey &&
          columns.some((column) => column.key === data.displayFieldKey)
            ? data.displayFieldKey
            : undefined) ||
          columns[0]?.key ||
          '';
        this.selectedKey.set(nextKey);

        if (this.isPrimarySecondary()) {
          const preferredSecondary =
            this.dialogData.selectedSecondaryDisplayFieldKey?.trim();
          const secondaryFallback =
            columns.find((column) => column.key !== nextKey)?.key || '';
          const nextSecondary =
            (preferredSecondary &&
            preferredSecondary !== nextKey &&
            columns.some((column) => column.key === preferredSecondary)
              ? preferredSecondary
              : undefined) || secondaryFallback;
          this.selectedSecondaryKey.set(nextSecondary);
        }
      },
    });
  }
}
